import { classifyBucket, IDENTITY_REQUIREMENT_LABELS, type Id } from "@repo/db"
import { SimplePdfViewer } from "@repo/docs"
import { Badge } from "@repo/shad/ui/badge"
import { Button } from "@repo/shad/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/shad/ui/card"
import { Skeleton } from "@repo/shad/ui/skeleton"
import { IconFileInfo } from "@tabler/icons-react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ChevronLeft, Download, Layout, Plus, Send } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { DocsFeatureDisabledState } from "@/components/docs/docs-feature-disabled-state"
import { useDocsFeatureEnabled } from "@/features/docs/feature-gate"
import { useTemplateDetails } from "@/hooks/use-template-details"
import { useTemplateVersioning } from "@/hooks/use-template-versioning"

import { TemplateDetailsForm } from "../../../../components/deal-packets/templates/template-details-form"
import { WebTemplateEditorCard } from "../../../../components/deal-packets/templates/web-template-editor-card"
import { SendFormDialog } from "../../../../components/documents/SendFormDialog"

export const Route = createFileRoute("/$dealership/deal-packets/templates/$templateId/")({
  staticData: {
    breadcrumb: false,
  },
  component: TemplateDetailPage,
})

function TemplateDetailPage() {
  const navigate = useNavigate()
  const context = Route.useRouteContext()
  const { templateId: routeTemplateId } = Route.useParams()
  const dealershipSlug =
    context && "dealership" in context && context.dealership ? context.dealership.slug : ""
  const dealershipId =
    context && "dealership" in context && context.dealership ? context.dealership._id : undefined
  const { enabled: docsEnabled, isPending: docsFeaturePending } =
    useDocsFeatureEnabled(dealershipId)

  if (docsFeaturePending) {
    return <TemplateDetailSkeleton />
  }

  if (!docsEnabled) {
    return (
      <DocsFeatureDisabledState
        title="Template details are turned off"
        description="This docs workflow is disabled for this dealership. Enable dealSigningDocs before viewing or editing templates."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate({
                to: "/$dealership/deal-packets",
                params: { dealership: dealershipSlug },
              })
            }}
          >
            Back to Deal Signing
          </Button>
        }
      />
    )
  }

  return (
    <EnabledTemplateDetailPage
      dealershipId={dealershipId}
      dealershipSlug={dealershipSlug}
      routeTemplateId={routeTemplateId as Id<"documentTemplates">}
    />
  )
}

function EnabledTemplateDetailPage({
  dealershipId,
  dealershipSlug,
  routeTemplateId,
}: {
  dealershipId?: Id<"dealerships">
  dealershipSlug: string
  routeTemplateId: Id<"documentTemplates">
}) {
  const navigate = useNavigate()
  const [sendFormDialogOpen, setSendFormDialogOpen] = useState(false)
  const [activeTemplateId, setActiveTemplateId] = useState(routeTemplateId)
  const editableTemplateIdRef = useRef<Id<"documentTemplates"> | null>(null)
  const templateRef = useRef<{
    _id: Id<"documentTemplates">
    name: string
    templateScope?: "global" | "dealership" | "deal"
    templateFormat?: "pdf" | "web"
  } | null>(null)
  const { isCreatingEditableVersion, createEditableVersion } = useTemplateVersioning()

  useEffect(() => {
    editableTemplateIdRef.current = null
    setActiveTemplateId(routeTemplateId)
  }, [routeTemplateId])

  const goToTemplate = useCallback(
    (nextTemplateId: Id<"documentTemplates">, replace = true) => {
      setActiveTemplateId(nextTemplateId)
      void navigate({
        to: "/$dealership/deal-packets/templates/$templateId",
        params: { dealership: dealershipSlug, templateId: nextTemplateId },
        replace,
      })
    },
    [dealershipSlug, navigate],
  )

  const handleTemplateIdChange = useCallback(
    (nextTemplateId: Id<"documentTemplates">) => {
      editableTemplateIdRef.current = nextTemplateId
      goToTemplate(nextTemplateId)
    },
    [goToTemplate],
  )

  const ensureEditableTemplate = useCallback(async () => {
    if (editableTemplateIdRef.current) {
      return {
        templateId: editableTemplateIdRef.current,
        created: false,
      }
    }

    const currentTemplate = templateRef.current
    if (!currentTemplate) {
      return {
        templateId: activeTemplateId,
        created: false,
      }
    }

    const result = await createEditableVersion(currentTemplate)
    if (result.created) {
      editableTemplateIdRef.current = result.templateId
    }
    return result
  }, [activeTemplateId, createEditableVersion])

  const {
    template,
    localRecord: editRecord,
    saveStatus: detailsSaveStatus,
    updateField: updateDetail,
  } = useTemplateDetails(activeTemplateId, {
    ensureEditableTemplate,
    onTemplateIdChange: handleTemplateIdChange,
  })

  templateRef.current = template ?? null

  const handleBack = () => {
    void navigate({
      to: "/$dealership/deal-packets/templates",
      params: { dealership: dealershipSlug },
    })
  }

  const handleCloneToDealership = async () => {
    if (!template) return

    const result = await createEditableVersion(template)
    editableTemplateIdRef.current = result.templateId

    if (!result.created) {
      goToTemplate(result.templateId, false)
      return
    }

    const isWebTemplate = (template.templateFormat ?? "pdf") === "web"
    if (isWebTemplate) {
      goToTemplate(result.templateId, false)
      return
    }

    setActiveTemplateId(result.templateId)
    void navigate({
      to: "/$dealership/deal-packets/templates/$templateId/fields",
      params: { dealership: dealershipSlug, templateId: result.templateId },
      replace: false,
    })
  }

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const signingRulesBadge = (() => {
    const bucket = classifyBucket({
      esignAllowed: editRecord.esignAllowed,
      requiresWetSignature: editRecord.requiresWetSignature || undefined,
      requiresNotary: editRecord.requiresNotary || undefined,
    })

    if (bucket === "remoteEsign") {
      return {
        label: "Remote e-sign",
        variant: "secondary" as const,
      }
    }

    if (bucket === "wetSignFollowUp") {
      return {
        label: "Remote review, sign offline",
        variant: "secondary" as const,
      }
    }

    return {
      label: "In-person only",
      variant: "destructive" as const,
    }
  })()

  if (!template) {
    return <TemplateDetailSkeleton />
  }

  const isSharedTemplate = template.templateScope === "global"
  const isWebTemplate = (template.templateFormat ?? "pdf") === "web"

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={handleBack} className="mb-4">
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Templates
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-bold">{template.name}</h1>
              {template.sourceTemplateId ? (
                <Badge variant="outline">Dealership version</Badge>
              ) : null}
              {isSharedTemplate ? <Badge variant="outline">Shared template</Badge> : null}
            </div>
            {template.description && (
              <p className="text-muted-foreground mt-2">{template.description}</p>
            )}
          </div>

          {isSharedTemplate ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void handleCloneToDealership()
              }}
              disabled={isCreatingEditableVersion}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Dealership Version
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {isWebTemplate ? (
            <WebTemplateEditorCard
              templateId={activeTemplateId}
              disabled={isCreatingEditableVersion}
              isSharedTemplate={isSharedTemplate}
              ensureEditableTemplate={ensureEditableTemplate}
              onTemplateIdChange={handleTemplateIdChange}
            />
          ) : (
            <SimplePdfViewer templateId={activeTemplateId} className="h-[800px]" />
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="gap-2">
              <div className="space-y-1.5">
                <CardTitle className="flex items-center gap-2">
                  <IconFileInfo className="size-4" />
                  Template Details
                </CardTitle>
                <CardDescription>
                  {isSharedTemplate
                    ? "This shared template stays read-only. Your first edit creates a dealership version."
                    : "Edit the name, category, and signing rules for this template."}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-1">
                <Badge variant={template.isActive ? "default" : "secondary"}>
                  {template.isActive ? "Active" : "Archived"}
                </Badge>
                <Badge variant={signingRulesBadge.variant}>{signingRulesBadge.label}</Badge>
                {editRecord.identityRequirement !== "none" && (
                  <Badge variant="outline">
                    {IDENTITY_REQUIREMENT_LABELS[editRecord.identityRequirement]}
                  </Badge>
                )}
              </div>

              {!isWebTemplate ? (
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(template.fileSize)}
                  {" \u00b7 "}
                  {template.pageCount} page{template.pageCount !== 1 ? "s" : ""}
                  {" \u00b7 "}
                  Created {formatDate(template.createdAt)}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Created {formatDate(template.createdAt)}
                </p>
              )}

              <TemplateDetailsForm
                record={editRecord}
                saveStatus={detailsSaveStatus}
                disabled={isCreatingEditableVersion}
                showDocumentType={!isWebTemplate}
                showStates={!isWebTemplate}
                onUpdate={updateDetail}
              />

              {isSharedTemplate ? (
                <p className="text-xs text-muted-foreground">
                  Prefer an explicit version first? Create one now and keep the shared original
                  available for future dealership variants.
                </p>
              ) : null}

              {isSharedTemplate ? (
                <Button
                  className="w-full justify-start"
                  onClick={() => {
                    void handleCloneToDealership()
                  }}
                  disabled={isCreatingEditableVersion}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Dealership Version
                </Button>
              ) : null}

              {!isWebTemplate ? (
                <div className="space-y-2 border-t pt-4">
                  {!isSharedTemplate ? (
                    <Button
                      className="w-full justify-start"
                      onClick={() => setSendFormDialogOpen(true)}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Send to Customer
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    disabled={isCreatingEditableVersion}
                    onClick={() =>
                      navigate({
                        to: "/$dealership/deal-packets/templates/$templateId/fields",
                        params: { dealership: dealershipSlug, templateId: activeTemplateId },
                      })
                    }
                  >
                    <Layout className="mr-2 h-4 w-4" />
                    Map Fields
                  </Button>
                  <Button variant="outline" className="w-full justify-start">
                    <Download className="mr-2 h-4 w-4" />
                    Download Original
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {!isWebTemplate ? (
        <SendFormDialog
          open={sendFormDialogOpen}
          onOpenChange={setSendFormDialogOpen}
          templateId={activeTemplateId}
          templateName={template.name}
          dealershipId={dealershipId ?? ("" as Id<"dealerships">)}
        />
      ) : null}
    </div>
  )
}

function TemplateDetailSkeleton() {
  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <Skeleton className="mb-4 h-8 w-36" />
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-9 w-20" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Skeleton className="h-[800px] w-full rounded-lg" />
        </div>
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-36" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-5 w-32" />
                </div>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-40" />
            </CardHeader>
            <CardContent className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
